import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  listFieldBoys,
  getFieldBoyStats,
  assignShopsToFieldBoy,
  reassignShop,
  listVisits,
  assignTicket,
  listFieldIssues,
  createFieldTask,
  listFieldTasks,
  patchFieldTask,
  searchShopsForPicker,
  getAdminShop
} from '../../services/fieldOpsAdminApi';

const TABS = [
  { id: 'field-boys', label: 'Field Boys & shops' },
  { id: 'visits', label: 'Visits' },
  { id: 'tasks', label: 'Field tasks' },
  { id: 'issues', label: 'Issues & tickets' }
];

const FIELD_TASK_STATUS = ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED', 'OVERDUE'];
const VISIT_TYPES = ['STANDARD_AUDIT', 'MAINTENANCE', 'PROMO_SETUP', 'ONBOARDING'];
const VISIT_OUTCOMES = ['ALL_GOOD', 'ISSUE_FOUND', 'ONBOARDED', 'FOLLOW_UP_NEEDED'];
const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const TICKET_STATUSES = ['Open', 'In Progress', 'Resolved', 'Closed'];

/** Stable default for FieldBoySelect.excludeIds so useMemo does not invalidate every render */
const NO_EXCLUDE_IDS = [];

function formatDt(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return String(iso);
  }
}

function errMsg(e) {
  return e?.response?.data?.message || e?.message || 'Request failed';
}

function fbLabel(fb) {
  if (!fb) return '';
  const name = fb.userId?.fullName || 'Unknown';
  const zone = fb.zone ? ` · ${fb.zone}` : '';
  return `${fb.employeeCode} — ${name}${zone}`;
}

/** Normalize Mongo _id (string, ObjectId, or rare `{ $oid }`) for comparisons and <option value>. */
function normalizeDocId(id) {
  if (id == null || id === '') return '';
  if (typeof id === 'object') {
    if (id.$oid) return String(id.$oid);
    return String(id);
  }
  return String(id);
}

/** Logged-in admin panel actor (employee or legacy user) for assignedBy on field tasks */
function formatVisitShopLabel(visit) {
  if (!visit) return '—';
  if (visit.shopLabel) return visit.shopLabel;
  if (visit.shopName) {
    const loc = [visit.shopCity, visit.shopState].filter(Boolean).join(', ');
    return loc ? `${visit.shopName} · ${loc}` : visit.shopName;
  }
  const id = normalizeDocId(visit.shopId);
  return id ? `…${id.slice(-6)}` : '—';
}

function getLoggedInActorId() {
  try {
    const cached = localStorage.getItem('kd_admin_employee_profile');
    if (cached) {
      const profile = JSON.parse(cached);
      const fromCache = normalizeDocId(profile._id || profile.id);
      if (fromCache) return fromCache;
    }
  } catch (_) {
    /* ignore */
  }
  return '';
}

function humanizeCamelLabel(key) {
  return String(key)
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function formatEnumLabel(status) {
  return String(status)
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Loads all field boys (paginated server-side, merged client-side) for dropdowns */
function useFieldBoyDirectory(showToast) {
  const [fieldBoys, setFieldBoys] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const merged = [];
      let page = 1;
      const limit = 100;
      let reportedTotal = 0;
      for (;;) {
        const { data } = await listFieldBoys({ page, limit });
        if (!data.success) {
          showToast(data.message || 'Could not load field boys list', 'error');
          break;
        }
        const chunk = data.data || [];
        merged.push(...chunk);
        reportedTotal = data.total ?? merged.length;
        if (chunk.length < limit || merged.length >= reportedTotal) break;
        page += 1;
        if (page > 50) break;
      }
      setFieldBoys(merged);
    } catch (e) {
      showToast(errMsg(e), 'error');
      setFieldBoys([]);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const byId = useMemo(() => {
    const m = new Map();
    fieldBoys.forEach((fb) => m.set(normalizeDocId(fb._id), fb));
    return m;
  }, [fieldBoys]);

  return useMemo(
    () => ({ fieldBoys, loading, refresh, byId }),
    [fieldBoys, loading, refresh, byId]
  );
}

function FieldBoySelect({
  label,
  value,
  onChange,
  fieldBoys,
  loading,
  excludeIds = NO_EXCLUDE_IDS,
  allowEmpty = true,
  emptyLabel = 'Any field boy',
  disabled = false,
  selectClassName = ''
}) {
  const exclude = useMemo(() => new Set(excludeIds.map(normalizeDocId)), [excludeIds]);
  const opts = fieldBoys.filter((fb) => !exclude.has(normalizeDocId(fb._id)));
  const selectCls = ['h-10 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white', selectClassName]
    .filter(Boolean)
    .join(' ');
  return (
    <label className={`block text-sm min-w-0 ${disabled ? 'opacity-60' : ''}`}>
      <span className="block text-slate-600 mb-1">{label}</span>
      <select
        className={selectCls}
        value={value}
        disabled={disabled || loading}
        onChange={(e) => onChange(e.target.value)}
        required={!allowEmpty}
      >
        {allowEmpty ? (
          <option value="">{loading ? 'Loading field boys…' : emptyLabel}</option>
        ) : (
          <option value="" disabled={!loading}>
            {loading ? 'Loading field boys…' : emptyLabel}
          </option>
        )}
        {opts.map((fb) => {
          const vid = normalizeDocId(fb._id);
          return (
            <option key={vid} value={vid}>
              {fbLabel(fb)}
            </option>
          );
        })}
      </select>
    </label>
  );
}

/** Search shops and pick one; results open as an overlay (dropdown), not inline in the layout grid. */
function ShopPickerSingle({ label, value, onChange, showToast, allowClear = true }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [pickedLabel, setPickedLabel] = useState('');
  const [panelOpen, setPanelOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!value) {
      setPickedLabel('');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getAdminShop(value);
        const shop = data?.data ?? data;
        if (cancelled || !shop) return;
        const city = shop.address?.city || shop.city;
        setPickedLabel(`${shop.name || 'Shop'}${city ? ` — ${city}` : ''}`);
      } catch {
        if (!cancelled) setPickedLabel('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [value]);

  useEffect(() => {
    if (value || !panelOpen) return undefined;
    const t = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const { data } = await searchShopsForPicker({
          search: query.trim(),
          page: 1,
          limit: 35
        });
        const list = data?.data;
        setResults(Array.isArray(list) ? list : []);
      } catch (e) {
        showToast(errMsg(e), 'error');
        setResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 320);
    return () => clearTimeout(t);
  }, [query, value, panelOpen, showToast]);

  useEffect(() => {
    if (!panelOpen || value) return undefined;
    const close = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setPanelOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [panelOpen, value]);

  return (
    <div className="relative text-sm min-w-0" ref={containerRef}>
      <span className="block text-slate-600 mb-1">{label}</span>
      {value ? (
        <div className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 flex items-center gap-2">
          <span className="flex-1 truncate text-slate-800 text-sm">{pickedLabel || 'Selected shop'}</span>
          {allowClear && (
            <button
              type="button"
              className="text-indigo-600 text-xs font-medium shrink-0"
              onClick={() => {
                onChange('');
                setQuery('');
                setPanelOpen(false);
              }}
            >
              Change
            </button>
          )}
        </div>
      ) : (
        <>
          <input
            className="w-full h-10 border border-slate-300 rounded-lg px-3 text-sm"
            placeholder="Search shop name, city…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setPanelOpen(true)}
          />
          {panelOpen && (
            <ul className="absolute z-[100] left-0 right-0 top-full mt-1 max-h-52 overflow-y-auto border border-slate-200 rounded-lg bg-white shadow-lg divide-y divide-slate-100">
              {searchLoading ? (
                <li className="px-3 py-2 text-slate-500 text-xs">Searching…</li>
              ) : results.length === 0 ? (
                <li className="px-3 py-2 text-slate-500 text-xs">Type to search or pick from results</li>
              ) : (
                results.map((s) => {
                  const city = s.address?.city || s.city;
                  const sid = normalizeDocId(s._id);
                  return (
                    <li key={sid}>
                      <button
                        type="button"
                        className="w-full text-left px-3 py-2 hover:bg-indigo-50 text-sm"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          onChange(sid);
                          setPickedLabel(`${s.name || 'Shop'}${city ? ` — ${city}` : ''}`);
                          setQuery('');
                          setPanelOpen(false);
                        }}
                      >
                        <span className="font-medium text-slate-900">{s.name || '—'}</span>
                        {city && <span className="text-slate-500"> · {city}</span>}
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

export default function FieldBoyOperations() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.some((t) => t.id === searchParams.get('tab')) ? searchParams.get('tab') : 'field-boys';
  const setTab = (id) => {
    setSearchParams({ tab: id });
  };

  const [toast, setToast] = useState(null);
  const showToast = useCallback((message, type = 'ok') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const fieldBoyDir = useFieldBoyDirectory(showToast);

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 rounded-lg px-4 py-3 shadow-lg text-sm ${
            toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-slate-800 text-white'
          }`}
        >
          {toast.message}
        </div>
      )}

      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">Field Boy — Field ops</h1>
        <p className="text-slate-600 mt-1 text-sm">
          Admin tools for field boys, shop assignment, visits, tasks, and field-ops tickets.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-3 mb-6">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === t.id ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'field-boys' && <FieldBoysTab showToast={showToast} fieldBoyDir={fieldBoyDir} />}
      {tab === 'visits' && <VisitsTab showToast={showToast} fieldBoyDir={fieldBoyDir} />}
      {tab === 'tasks' && <TasksTab showToast={showToast} fieldBoyDir={fieldBoyDir} />}
      {tab === 'issues' && <IssuesTab showToast={showToast} fieldBoyDir={fieldBoyDir} />}
    </div>
  );
}

function FieldBoysTab({ showToast, fieldBoyDir }) {
  const [status, setStatus] = useState('');
  const [zone, setZone] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [statsOpen, setStatsOpen] = useState(false);
  const [statsFor, setStatsFor] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignFb, setAssignFb] = useState(null);
  const [shopSearch, setShopSearch] = useState('');
  const [shopResults, setShopResults] = useState([]);
  const [shopSearchLoading, setShopSearchLoading] = useState(false);
  const [selectedShopIds, setSelectedShopIds] = useState(() => new Set());
  const [assignSubmitting, setAssignSubmitting] = useState(false);

  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignOldFb, setReassignOldFb] = useState(null);
  const [reassignShopId, setReassignShopId] = useState('');
  const [reassignNewFbId, setReassignNewFbId] = useState('');
  const [reassignSubmitting, setReassignSubmitting] = useState(false);
  const { fieldBoys: allFieldBoys, loading: fbListLoading } = fieldBoyDir;

  const reassignTargetChoices = useMemo(() => {
    if (!reassignOldFb) return [];
    const ex = normalizeDocId(reassignOldFb._id);
    return allFieldBoys.filter((fb) => normalizeDocId(fb._id) !== ex);
  }, [allFieldBoys, reassignOldFb]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listFieldBoys({
        page,
        limit,
        ...(status && { status }),
        ...(zone.trim() && { zone: zone.trim() })
      });
      if (data.success) {
        setRows(data.data || []);
        setTotal(data.total || 0);
      } else {
        showToast(data.message || 'Failed to load', 'error');
      }
    } catch (e) {
      showToast(errMsg(e), 'error');
    } finally {
      setLoading(false);
    }
  }, [page, limit, status, zone, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const openStats = async (fb) => {
    setStatsFor(fb);
    setStatsOpen(true);
    setStatsData(null);
    setStatsLoading(true);
    try {
      const { data } = await getFieldBoyStats(fb._id);
      if (data.success) setStatsData(data.data);
      else showToast(data.message || 'Failed to load stats', 'error');
    } catch (e) {
      showToast(errMsg(e), 'error');
    } finally {
      setStatsLoading(false);
    }
  };

  const searchShops = async () => {
    setShopSearchLoading(true);
    try {
      const { data } = await searchShopsForPicker({
        search: shopSearch.trim(),
        page: 1,
        limit: 25
      });
      const list = data?.data;
      setShopResults(Array.isArray(list) ? list : []);
    } catch (e) {
      showToast(errMsg(e), 'error');
      setShopResults([]);
    } finally {
      setShopSearchLoading(false);
    }
  };

  useEffect(() => {
    if (!assignOpen) return undefined;
    const t = setTimeout(() => {
      searchShops();
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopSearch, assignOpen]);

  const toggleShop = (id) => {
    const nid = normalizeDocId(id);
    setSelectedShopIds((prev) => {
      const next = new Set(prev);
      if (next.has(nid)) next.delete(nid);
      else next.add(nid);
      return next;
    });
  };

  const submitAssign = async () => {
    if (!assignFb || !selectedShopIds.size) {
      showToast('Select at least one shop', 'error');
      return;
    }
    setAssignSubmitting(true);
    try {
      const { data } = await assignShopsToFieldBoy({
        fieldBoyId: assignFb._id,
        shopIds: Array.from(selectedShopIds)
      });
      if (data.success) {
        showToast(data.message || 'Shops assigned');
        setAssignOpen(false);
        setSelectedShopIds(new Set());
        load();
      } else showToast(data.message || 'Failed', 'error');
    } catch (e) {
      showToast(errMsg(e), 'error');
    } finally {
      setAssignSubmitting(false);
    }
  };

  const submitReassign = async () => {
    if (!reassignOldFb || !reassignShopId.trim() || !reassignNewFbId.trim()) {
      showToast('Select shop and new field boy', 'error');
      return;
    }
    setReassignSubmitting(true);
    try {
      const { data } = await reassignShop({
        oldFieldBoyId: reassignOldFb._id,
        newFieldBoyId: reassignNewFbId.trim(),
        shopId: reassignShopId.trim()
      });
      if (data.success) {
        showToast(data.message || 'Reassigned');
        setReassignOpen(false);
        setReassignShopId('');
        setReassignNewFbId('');
        load();
      } else showToast(data.message || 'Failed', 'error');
    } catch (e) {
      showToast(errMsg(e), 'error');
    } finally {
      setReassignSubmitting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <label className="text-sm">
          <span className="block text-slate-600 mb-1">Account status</span>
          <select
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm"
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value);
            }}
          >
            <option value="">Any</option>
            <option value="PENDING">PENDING</option>
            <option value="ACTIVE">ACTIVE</option>
            <option value="INACTIVE">INACTIVE</option>
            <option value="SUSPENDED">SUSPENDED</option>
          </select>
        </label>
        <label className="text-sm flex-1 min-w-[140px]">
          <span className="block text-slate-600 mb-1">Zone contains</span>
          <input
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
            value={zone}
            onChange={(e) => {
              setPage(1);
              setZone(e.target.value);
            }}
            placeholder="e.g. West"
          />
        </label>
        <button
          type="button"
          onClick={load}
          className="px-4 py-2 bg-slate-800 text-white rounded-lg text-sm font-medium"
        >
          Refresh
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              <th className="px-3 py-2 font-medium">Code</th>
              <th className="px-3 py-2 font-medium">User</th>
              <th className="px-3 py-2 font-medium">Zone</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">Shops</th>
              <th className="px-3 py-2 font-medium">Tasks by status</th>
              <th className="px-3 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                  No field boys found
                </td>
              </tr>
            ) : (
              rows.map((fb) => (
                <tr key={fb._id} className="border-t border-slate-100 hover:bg-slate-50/80">
                  <td className="px-3 py-2 font-mono text-xs">{fb.employeeCode}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-900">{fb.userId?.fullName || '—'}</div>
                    <div className="text-xs text-slate-500">{fb.userId?.phone || ''}</div>
                  </td>
                  <td className="px-3 py-2">{fb.zone || '—'}</td>
                  <td className="px-3 py-2">
                    <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium">
                      {fb.accountStatus}
                    </span>
                  </td>
                  <td className="px-3 py-2">{fb.assignedShopsCount ?? 0}</td>
                  <td className="px-3 py-2 text-xs text-slate-600 max-w-[200px]">
                    {fb.taskStats
                      ? Object.entries(fb.taskStats)
                          .map(([k, v]) => `${k}: ${v}`)
                          .join(', ')
                      : '—'}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap space-x-2">
                    <button
                      type="button"
                      className="text-indigo-600 hover:underline text-xs font-medium"
                      onClick={() => openStats(fb)}
                    >
                      Stats
                    </button>
                    <button
                      type="button"
                      className="text-indigo-600 hover:underline text-xs font-medium"
                      onClick={() => {
                        setAssignFb(fb);
                        setSelectedShopIds(new Set());
                        setShopSearch('');
                        setAssignOpen(true);
                      }}
                    >
                      Assign shops
                    </button>
                    <button
                      type="button"
                      className="text-indigo-600 hover:underline text-xs font-medium"
                      onClick={() => {
                        setReassignOldFb(fb);
                        setReassignShopId('');
                        setReassignNewFbId('');
                        setReassignOpen(true);
                      }}
                    >
                      Reassign shop
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center mt-4 text-sm text-slate-600">
        <span>
          Page {page} of {totalPages} ({total} total)
        </span>
        <div className="space-x-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1 rounded border border-slate-300 disabled:opacity-40"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1 rounded border border-slate-300 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>

      {statsOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={() => setStatsOpen(false)}>
          <div
            className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold">Field boy stats</h3>
              <button type="button" className="text-slate-500 hover:text-slate-800" onClick={() => setStatsOpen(false)}>
                ✕
              </button>
            </div>
            {statsFor && (
              <p className="text-sm text-slate-600 mb-4">
                {statsFor.employeeCode} — {statsFor.userId?.fullName}
              </p>
            )}
            {statsLoading ? (
              <p className="text-slate-500">Loading…</p>
            ) : statsData ? (
              <div className="space-y-5 text-sm">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <StatCard label="Visits completed" value={statsData.visitsCompleted} />
                  <StatCard label="Issues resolved" value={statsData.issuesResolved} />
                  <StatCard label="Issues reported (as FB user)" value={statsData.issuesReportedAsFieldBoy} />
                </div>
                <StatsExperienceSection metrics={statsData.experienceMetrics} />
                <StatsTaskStatusSection tasksByStatus={statsData.tasksByStatus} />
              </div>
            ) : (
              <p className="text-slate-500">No data</p>
            )}
          </div>
        </div>
      )}

      {assignOpen && assignFb && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={() => setAssignOpen(false)}>
          <div
            className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-1">Assign shops</h3>
            <p className="text-sm text-slate-600 mb-4">
              To <strong>{assignFb.employeeCode}</strong> — {assignFb.userId?.fullName}
            </p>
            <label className="block text-sm mb-2">
              <span className="text-slate-600">Search shops</span>
              <input
                className="mt-1 w-full h-10 border border-slate-300 rounded-lg px-3 text-sm"
                value={shopSearch}
                onChange={(e) => setShopSearch(e.target.value)}
                placeholder="Name, city, phone…"
              />
            </label>
            <p className="text-xs text-slate-500 mb-2">{shopSearchLoading ? 'Searching…' : `${shopResults.length} results`}</p>
            <ul className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-56 overflow-y-auto mb-4 bg-white">
              {shopResults.map((s) => {
                const id = normalizeDocId(s._id);
                const checked = selectedShopIds.has(id);
                const city = s.address?.city || s.city;
                const state = s.address?.state || s.state;
                const locality = [city, state].filter(Boolean).join(', ');
                const subtitle = locality || s.contactPhone || s.shopPhone || s.contactEmail || '';
                return (
                  <li key={id}>
                    <label className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50 text-left w-full">
                      <input
                        type="checkbox"
                        className="h-4 w-4 shrink-0 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                        checked={checked}
                        onChange={() => toggleShop(id)}
                      />
                      <span className="min-w-0 flex-1 text-left">
                        <span className="block font-medium text-slate-900 truncate">{s.name || '—'}</span>
                        {subtitle ? (
                          <span className="block text-xs text-slate-500 truncate">{subtitle}</span>
                        ) : (
                          <span className="block text-xs text-slate-400">No location on file</span>
                        )}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
            <div className="flex gap-2 justify-end">
              <button type="button" className="px-4 py-2 rounded-lg border border-slate-300 text-sm" onClick={() => setAssignOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                disabled={assignSubmitting}
                onClick={submitAssign}
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium disabled:opacity-50"
              >
                {assignSubmitting ? 'Saving…' : 'Assign selected'}
              </button>
            </div>
          </div>
        </div>
      )}

      {reassignOpen && reassignOldFb && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={() => setReassignOpen(false)}>
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-1">Reassign shop</h3>
            <p className="text-sm text-slate-600 mb-4">
              From <strong>{reassignOldFb.employeeCode}</strong> to another field boy
            </p>
            <div className="space-y-4">
            <div className="mb-4">
              <ShopPickerSingle
                label="Shop to move"
                value={reassignShopId}
                onChange={(id) => setReassignShopId(id)}
                showToast={showToast}
              />
            </div>
            <FieldBoySelect
              label="Assign to field boy"
              value={reassignNewFbId}
              onChange={setReassignNewFbId}
              fieldBoys={reassignTargetChoices}
              loading={fbListLoading}
              allowEmpty
              emptyLabel="Choose field boy…"
            />
            {!fbListLoading && reassignTargetChoices.length === 0 && (
              <p className="text-xs text-amber-900 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mt-2">
                No other field boys are available. Register or approve another field boy before reassigning.
              </p>
            )}
            </div>
            <div className="flex gap-2 justify-end">
              <button type="button" className="px-4 py-2 rounded-lg border border-slate-300 text-sm" onClick={() => setReassignOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                disabled={reassignSubmitting}
                onClick={submitReassign}
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium disabled:opacity-50"
              >
                {reassignSubmitting ? 'Saving…' : 'Reassign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-xl font-semibold text-slate-900">{value ?? '—'}</div>
    </div>
  );
}

function StatsExperienceSection({ metrics }) {
  const entries = Object.entries(metrics || {});
  if (entries.length === 0) {
    return (
      <div>
        <div className="font-medium text-slate-800 mb-2">Experience metrics</div>
        <p className="text-slate-500 text-sm">No metrics recorded yet.</p>
      </div>
    );
  }
  return (
    <div>
      <div className="font-medium text-slate-800 mb-2">Experience metrics</div>
      <div className="grid grid-cols-2 gap-2">
        {entries.map(([k, v]) => (
          <div
            key={k}
            className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white px-3 py-3 shadow-sm"
          >
            <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{humanizeCamelLabel(k)}</div>
            <div className="text-xl font-semibold text-slate-900 mt-1 tabular-nums">{v ?? '—'}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatsTaskStatusSection({ tasksByStatus }) {
  const entries = Object.entries(tasksByStatus || {});
  if (entries.length === 0) {
    return (
      <div>
        <div className="font-medium text-slate-800 mb-2">Tasks by status</div>
        <p className="text-slate-500 text-sm">No tasks in breakdown.</p>
      </div>
    );
  }
  return (
    <div>
      <div className="font-medium text-slate-800 mb-2">Tasks by status</div>
      <div className="flex flex-wrap gap-2">
        {entries.map(([status, count]) => (
          <span
            key={status}
            className="inline-flex items-center gap-2 rounded-full border border-indigo-100 bg-indigo-50/90 px-3 py-1.5 text-sm"
          >
            <span className="font-medium text-indigo-950">{formatEnumLabel(status)}</span>
            <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold tabular-nums text-indigo-700 shadow-sm">
              {count}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

function VisitsTab({ showToast, fieldBoyDir }) {
  const [fieldBoyId, setFieldBoyId] = useState('');
  const [shopId, setShopId] = useState('');
  const { fieldBoys, loading: fbLoading } = fieldBoyDir;
  const [visitType, setVisitType] = useState('');
  const [outcome, setOutcome] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listVisits({
        page,
        limit,
        ...(fieldBoyId.trim() && { fieldBoyId: fieldBoyId.trim() }),
        ...(shopId.trim() && { shopId: shopId.trim() }),
        ...(visitType && { visitType }),
        ...(outcome && { outcome }),
        ...(dateFrom && { dateFrom: new Date(dateFrom).toISOString() }),
        ...(dateTo && { dateTo: new Date(dateTo).toISOString() })
      });
      if (data.success) {
        setRows(data.data || []);
        setTotal(data.total || 0);
      } else showToast(data.message || 'Failed', 'error');
    } catch (e) {
      showToast(errMsg(e), 'error');
    } finally {
      setLoading(false);
    }
  }, [page, limit, fieldBoyId, shopId, visitType, outcome, dateFrom, dateTo, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const filterSelectCls =
    'h-10 w-full min-h-[2.5rem] border border-slate-300 rounded-lg px-3 text-sm bg-white';

  return (
    <div>
      <div className="flex flex-col gap-3 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
          <FieldBoySelect
            label="Field boy"
            value={fieldBoyId}
            onChange={(v) => {
              setPage(1);
              setFieldBoyId(v);
            }}
            fieldBoys={fieldBoys}
            loading={fbLoading}
            emptyLabel="All field boys"
          />
          <ShopPickerSingle
            label="Shop (optional filter)"
            value={shopId}
            onChange={(id) => {
              setPage(1);
              setShopId(id);
            }}
            showToast={showToast}
          />
          <div className="min-w-0">
            <span className="block text-slate-600 mb-1 text-sm">Visit type</span>
            <select
              className={filterSelectCls}
              value={visitType}
              onChange={(e) => {
                setPage(1);
                setVisitType(e.target.value);
              }}
            >
              <option value="">Any visit type</option>
              {VISIT_TYPES.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
          <div className="min-w-0">
            <span className="block text-slate-600 mb-1 text-sm">Outcome</span>
            <select
              className={filterSelectCls}
              value={outcome}
              onChange={(e) => {
                setPage(1);
                setOutcome(e.target.value);
              }}
            >
              <option value="">Any outcome</option>
              {VISIT_OUTCOMES.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <label className="block text-sm min-w-0">
            <span className="block text-slate-600 mb-1">From date</span>
            <input
              type="datetime-local"
              className={filterSelectCls}
              value={dateFrom}
              onChange={(e) => {
                setPage(1);
                setDateFrom(e.target.value);
              }}
            />
          </label>
          <label className="block text-sm min-w-0">
            <span className="block text-slate-600 mb-1">To date</span>
            <input
              type="datetime-local"
              className={filterSelectCls}
              value={dateTo}
              onChange={(e) => {
                setPage(1);
                setDateTo(e.target.value);
              }}
            />
          </label>
        </div>
      </div>
      <button type="button" onClick={load} className="mb-4 px-4 py-2 bg-slate-800 text-white rounded-lg text-sm">
        Apply filters
      </button>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              <th className="px-3 py-2 font-medium">Check-in</th>
              <th className="px-3 py-2 font-medium">Field boy</th>
              <th className="px-3 py-2 font-medium">Shop</th>
              <th className="px-3 py-2 font-medium">Type</th>
              <th className="px-3 py-2 font-medium">Outcome</th>
              <th className="px-3 py-2 font-medium">Notes</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-slate-500">
                  No visits
                </td>
              </tr>
            ) : (
              rows.map((v) => (
                <tr key={v._id} className="border-t border-slate-100">
                  <td className="px-3 py-2 whitespace-nowrap">{formatDt(v.checkInTime)}</td>
                  <td className="px-3 py-2 text-xs text-slate-800">
                    {fieldBoyDir.byId.get(normalizeDocId(v.fieldBoyId))
                      ? fbLabel(fieldBoyDir.byId.get(normalizeDocId(v.fieldBoyId)))
                      : `…${String(normalizeDocId(v.fieldBoyId)).slice(-6)}`}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-800 max-w-[180px]">
                    <span className="block truncate font-medium" title={formatVisitShopLabel(v)}>
                      {formatVisitShopLabel(v)}
                    </span>
                  </td>
                  <td className="px-3 py-2">{v.visitType}</td>
                  <td className="px-3 py-2">{v.outcome || '—'}</td>
                  <td className="px-3 py-2 max-w-xs truncate" title={v.notes}>
                    {v.notes || '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between items-center mt-4 text-sm text-slate-600">
        <span>
          Page {page} / {totalPages} ({total})
        </span>
        <div className="space-x-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1 rounded border border-slate-300 disabled:opacity-40"
          >
            Prev
          </button>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1 rounded border border-slate-300 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

function TasksTab({ showToast, fieldBoyDir }) {
  const { user } = useAuth();
  const assignedByActorId = useMemo(() => {
    const fromUser = user ? normalizeDocId(user._id || user.id) : '';
    return fromUser || getLoggedInActorId();
  }, [user]);

  const [assignedTo, setAssignedTo] = useState('');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [shopId, setShopId] = useState('');
  const [taskType, setTaskType] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const { fieldBoys, loading: fbLoading, byId } = fieldBoyDir;

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    assignedTo: '',
    shopId: '',
    taskType: 'STANDARD_AUDIT',
    priority: 'MEDIUM',
    dueDate: ''
  });
  const [saving, setSaving] = useState(false);

  const [editTask, setEditTask] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listFieldTasks({
        page,
        limit,
        ...(assignedTo.trim() && { assignedTo: assignedTo.trim() }),
        ...(status && { status }),
        ...(priority && { priority }),
        ...(shopId.trim() && { shopId: shopId.trim() }),
        ...(taskType && { taskType })
      });
      if (data.success) {
        setRows(data.data || []);
        setTotal(data.total || 0);
      } else showToast(data.message || 'Failed', 'error');
    } catch (e) {
      showToast(errMsg(e), 'error');
    } finally {
      setLoading(false);
    }
  }, [page, limit, assignedTo, status, priority, shopId, taskType, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const submitCreate = async () => {
    if (!form.title.trim() || !form.assignedTo.trim()) {
      showToast('Enter title and choose a field boy', 'error');
      return;
    }
    if (!assignedByActorId) {
      showToast('Could not determine logged-in user. Please sign in again.', 'error');
      return;
    }
    setSaving(true);
    try {
      const body = {
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        assignedTo: form.assignedTo.trim(),
        assignedBy: assignedByActorId,
        shopId: form.shopId.trim() || undefined,
        taskType: form.taskType,
        priority: form.priority,
        dueDate: form.dueDate ? new Date(form.dueDate).toISOString() : undefined
      };
      const { data } = await createFieldTask(body);
      if (data.success) {
        showToast('Task created');
        setCreateOpen(false);
        setForm({
          title: '',
          description: '',
          assignedTo: '',
          shopId: '',
          taskType: 'STANDARD_AUDIT',
          priority: 'MEDIUM',
          dueDate: ''
        });
        load();
      } else showToast(data.message || 'Failed', 'error');
    } catch (e) {
      showToast(errMsg(e), 'error');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (t) => {
    setEditTask(t);
    setEditForm({
      title: t.title,
      description: t.description || '',
      status: t.status,
      priority: t.priority,
      notes: t.notes || '',
      assignedTo: normalizeDocId(t.assignedTo),
      dueDate: t.dueDate ? new Date(t.dueDate).toISOString().slice(0, 16) : ''
    });
  };

  const submitEdit = async () => {
    if (!editTask) return;
    setEditSaving(true);
    try {
      const body = {
        title: editForm.title,
        description: editForm.description,
        status: editForm.status,
        priority: editForm.priority,
        notes: editForm.notes,
        assignedTo: editForm.assignedTo,
        dueDate: editForm.dueDate ? new Date(editForm.dueDate).toISOString() : null
      };
      const { data } = await patchFieldTask(editTask._id, body);
      if (data.success) {
        showToast('Task updated');
        setEditTask(null);
        load();
      } else showToast(data.message || 'Failed', 'error');
    } catch (e) {
      showToast(errMsg(e), 'error');
    } finally {
      setEditSaving(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const filterSelectCls =
    'h-10 w-full min-h-[2.5rem] border border-slate-300 rounded-lg px-3 text-sm bg-white';

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium"
        >
          New task
        </button>
        <button type="button" onClick={load} className="px-4 py-2 border border-slate-300 rounded-lg text-sm">
          Refresh
        </button>
      </div>

      <div className="flex flex-col gap-3 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
          <FieldBoySelect
            label="Assigned field boy"
            value={assignedTo}
            onChange={(v) => {
              setPage(1);
              setAssignedTo(v);
            }}
            fieldBoys={fieldBoys}
            loading={fbLoading}
            emptyLabel="All assignees"
          />
          <div className="min-w-0">
            <span className="block text-slate-600 mb-1 text-sm">Status</span>
            <select
              className={filterSelectCls}
              value={status}
              onChange={(e) => {
                setPage(1);
                setStatus(e.target.value);
              }}
            >
              <option value="">Any status</option>
              {FIELD_TASK_STATUS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="min-w-0">
            <span className="block text-slate-600 mb-1 text-sm">Priority</span>
            <select
              className={filterSelectCls}
              value={priority}
              onChange={(e) => {
                setPage(1);
                setPriority(e.target.value);
              }}
            >
              <option value="">Any priority</option>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start max-w-4xl">
          <ShopPickerSingle
            label="Shop (optional filter)"
            value={shopId}
            onChange={(id) => {
              setPage(1);
              setShopId(id);
            }}
            showToast={showToast}
          />
          <div className="min-w-0">
            <span className="block text-slate-600 mb-1 text-sm">Task type</span>
            <select
              className={filterSelectCls}
              value={taskType}
              onChange={(e) => {
                setPage(1);
                setTaskType(e.target.value);
              }}
            >
              <option value="">Any task type</option>
              {VISIT_TYPES.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              <th className="px-3 py-2 font-medium">Code</th>
              <th className="px-3 py-2 font-medium">Title</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">Priority</th>
              <th className="px-3 py-2 font-medium">Due</th>
              <th className="px-3 py-2 font-medium">Assigned to</th>
              <th className="px-3 py-2 font-medium" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                  No tasks
                </td>
              </tr>
            ) : (
              rows.map((t) => (
                <tr key={t._id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-mono text-xs">{t.taskCode}</td>
                  <td className="px-3 py-2 max-w-[200px]">
                    <div className="font-medium truncate">{t.title}</div>
                  </td>
                  <td className="px-3 py-2">{t.status}</td>
                  <td className="px-3 py-2">{t.priority}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs">{formatDt(t.dueDate)}</td>
                  <td className="px-3 py-2 text-xs text-slate-800 max-w-[160px] truncate" title={fbLabel(byId.get(normalizeDocId(t.assignedTo)))}>
                    {byId.get(normalizeDocId(t.assignedTo)) ? fbLabel(byId.get(normalizeDocId(t.assignedTo))) : `…${String(normalizeDocId(t.assignedTo)).slice(-6)}`}
                  </td>
                  <td className="px-3 py-2">
                    <button type="button" className="text-indigo-600 text-xs font-medium hover:underline" onClick={() => openEdit(t)}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center mt-4 text-sm text-slate-600">
        <span>
          Page {page} / {totalPages} ({total})
        </span>
        <div className="space-x-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1 rounded border border-slate-300 disabled:opacity-40"
          >
            Prev
          </button>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1 rounded border border-slate-300 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>

      {createOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={() => setCreateOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Create field task</h3>
            <div className="space-y-3 text-sm">
              <label className="block">
                <span className="text-slate-600">Title *</span>
                <input
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                />
              </label>
              <label className="block">
                <span className="text-slate-600">Description</span>
                <textarea
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                />
              </label>
              <FieldBoySelect
                label="Assign to field boy *"
                value={form.assignedTo}
                onChange={(v) => setForm((f) => ({ ...f, assignedTo: v }))}
                fieldBoys={fieldBoys}
                loading={fbLoading}
                allowEmpty={false}
                emptyLabel="Choose field boy…"
              />
              <div className="block">
                <ShopPickerSingle
                  label="Shop (optional)"
                  value={form.shopId}
                  onChange={(id) => setForm((f) => ({ ...f, shopId: id }))}
                  showToast={showToast}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="text-slate-600">Task type</span>
                  <select
                    className="mt-1 w-full border border-slate-300 rounded-lg px-2 py-2"
                    value={form.taskType}
                    onChange={(e) => setForm((f) => ({ ...f, taskType: e.target.value }))}
                  >
                    {VISIT_TYPES.map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="text-slate-600">Priority</span>
                  <select
                    className="mt-1 w-full border border-slate-300 rounded-lg px-2 py-2"
                    value={form.priority}
                    onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
                  >
                    <option value="LOW">LOW</option>
                    <option value="MEDIUM">MEDIUM</option>
                    <option value="HIGH">HIGH</option>
                    <option value="CRITICAL">CRITICAL</option>
                  </select>
                </label>
              </div>
              <label className="block">
                <span className="text-slate-600">Due date</span>
                <input
                  type="datetime-local"
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
                  value={form.dueDate}
                  onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))}
                />
              </label>
            </div>
            <div className="flex gap-2 justify-end mt-6">
              <button type="button" className="px-4 py-2 rounded-lg border border-slate-300 text-sm" onClick={() => setCreateOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={submitCreate}
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium disabled:opacity-50"
              >
                {saving ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editTask && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditTask(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-1">Edit task</h3>
            <p className="text-xs font-mono text-slate-500 mb-4">{editTask.taskCode}</p>
            <div className="space-y-3 text-sm">
              <label className="block">
                <span className="text-slate-600">Title</span>
                <input
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
                  value={editForm.title}
                  onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                />
              </label>
              <label className="block">
                <span className="text-slate-600">Description</span>
                <textarea
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
                  rows={2}
                  value={editForm.description}
                  onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                />
              </label>
              <label className="block">
                <span className="text-slate-600">Status</span>
                <select
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
                  value={editForm.status}
                  onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}
                >
                  {FIELD_TASK_STATUS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-slate-600">Priority</span>
                <select
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
                  value={editForm.priority}
                  onChange={(e) => setEditForm((f) => ({ ...f, priority: e.target.value }))}
                >
                  <option value="LOW">LOW</option>
                  <option value="MEDIUM">MEDIUM</option>
                  <option value="HIGH">HIGH</option>
                  <option value="CRITICAL">CRITICAL</option>
                </select>
              </label>
              <FieldBoySelect
                label="Assigned field boy"
                value={editForm.assignedTo}
                onChange={(v) => setEditForm((f) => ({ ...f, assignedTo: v }))}
                fieldBoys={fieldBoys}
                loading={fbLoading}
                allowEmpty={false}
                emptyLabel="Choose field boy…"
              />
              <label className="block">
                <span className="text-slate-600">Due date</span>
                <input
                  type="datetime-local"
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
                  value={editForm.dueDate}
                  onChange={(e) => setEditForm((f) => ({ ...f, dueDate: e.target.value }))}
                />
              </label>
              <label className="block">
                <span className="text-slate-600">Notes</span>
                <textarea
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
                  rows={2}
                  value={editForm.notes}
                  onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
                />
              </label>
            </div>
            <div className="flex gap-2 justify-end mt-6">
              <button type="button" className="px-4 py-2 rounded-lg border border-slate-300 text-sm" onClick={() => setEditTask(null)}>
                Cancel
              </button>
              <button
                type="button"
                disabled={editSaving}
                onClick={submitEdit}
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium disabled:opacity-50"
              >
                {editSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function IssuesTab({ showToast, fieldBoyDir }) {
  const [fieldBoyId, setFieldBoyId] = useState('');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [assignOpen, setAssignOpen] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [assignFbId, setAssignFbId] = useState('');
  const [assignSaving, setAssignSaving] = useState(false);

  const { fieldBoys, loading: fbLoading, byId } = fieldBoyDir;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listFieldIssues({
        page,
        limit,
        ...(fieldBoyId.trim() && { fieldBoyId: fieldBoyId.trim() }),
        ...(status && { status }),
        ...(priority && { priority })
      });
      if (data.success) {
        setRows(data.data || []);
        setTotal(data.total || 0);
      } else showToast(data.message || 'Failed', 'error');
    } catch (e) {
      showToast(errMsg(e), 'error');
    } finally {
      setLoading(false);
    }
  }, [page, limit, fieldBoyId, status, priority, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const submitAssign = async () => {
    if (!ticket || !assignFbId.trim()) {
      showToast('Choose a field boy', 'error');
      return;
    }
    setAssignSaving(true);
    try {
      const { data } = await assignTicket({ ticketId: ticket._id, fieldBoyId: assignFbId.trim() });
      if (data.success) {
        showToast('Ticket assigned (status set to In Progress)');
        setAssignOpen(false);
        setTicket(null);
        setAssignFbId('');
        load();
      } else showToast(data.message || 'Failed', 'error');
    } catch (e) {
      showToast(errMsg(e), 'error');
    } finally {
      setAssignSaving(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const filterSelectCls =
    'h-10 w-full min-h-[2.5rem] border border-slate-300 rounded-lg px-3 text-sm bg-white';

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div className="w-full sm:w-56 shrink-0 min-w-0">
          <FieldBoySelect
            label="Field boy (filter)"
            value={fieldBoyId}
            onChange={(v) => {
              setPage(1);
              setFieldBoyId(v);
            }}
            fieldBoys={fieldBoys}
            loading={fbLoading}
            emptyLabel="All (field-ops tickets)"
          />
        </div>
        <div className="w-full sm:w-44 shrink-0 min-w-0">
          <span className="block text-slate-600 mb-1 text-sm">Status</span>
          <select
            className={filterSelectCls}
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value);
            }}
          >
            <option value="">Any status</option>
            {TICKET_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className="w-full sm:w-44 shrink-0 min-w-0">
          <span className="block text-slate-600 mb-1 text-sm">Priority</span>
          <select
            className={filterSelectCls}
            value={priority}
            onChange={(e) => {
              setPage(1);
              setPriority(e.target.value);
            }}
          >
            <option value="">Any priority</option>
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
      </div>
      <button type="button" onClick={load} className="mb-4 px-4 py-2 bg-slate-800 text-white rounded-lg text-sm">
        Refresh
      </button>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              <th className="px-3 py-2 font-medium">Ticket</th>
              <th className="px-3 py-2 font-medium">Subject</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">Priority</th>
              <th className="px-3 py-2 font-medium">Source</th>
              <th className="px-3 py-2 font-medium">Assigned FB</th>
              <th className="px-3 py-2 font-medium" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                  No tickets
                </td>
              </tr>
            ) : (
              rows.map((t) => (
                <tr key={t._id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-mono text-xs">{t.ticketId || String(t._id).slice(-8)}</td>
                  <td className="px-3 py-2 max-w-[220px]">
                    <div className="truncate font-medium" title={t.subject}>
                      {t.subject || '—'}
                    </div>
                  </td>
                  <td className="px-3 py-2">{t.status}</td>
                  <td className="px-3 py-2">{t.priority || '—'}</td>
                  <td className="px-3 py-2 text-xs">{t.source || '—'}</td>
                  <td className="px-3 py-2 text-xs text-slate-800 max-w-[140px] truncate" title={fbLabel(byId.get(normalizeDocId(t.assignedFieldBoyId)))}>
                    {t.assignedFieldBoyId
                      ? byId.get(normalizeDocId(t.assignedFieldBoyId))
                        ? fbLabel(byId.get(normalizeDocId(t.assignedFieldBoyId)))
                        : `…${String(normalizeDocId(t.assignedFieldBoyId)).slice(-6)}`
                      : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      className="text-indigo-600 text-xs font-medium hover:underline"
                      onClick={() => {
                        setTicket(t);
                        setAssignFbId(t.assignedFieldBoyId ? normalizeDocId(t.assignedFieldBoyId) : '');
                        setAssignOpen(true);
                      }}
                    >
                      Assign
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center mt-4 text-sm text-slate-600">
        <span>
          Page {page} / {totalPages} ({total})
        </span>
        <div className="space-x-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1 rounded border border-slate-300 disabled:opacity-40"
          >
            Prev
          </button>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1 rounded border border-slate-300 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>

      {assignOpen && ticket && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={() => setAssignOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-2">Assign ticket to field boy</h3>
            <p className="text-sm text-slate-600 mb-4 line-clamp-2">{ticket.subject}</p>
            <FieldBoySelect
              label="Assign to field boy"
              value={assignFbId}
              onChange={setAssignFbId}
              fieldBoys={fieldBoys}
              loading={fbLoading}
              allowEmpty={false}
              emptyLabel="Choose field boy…"
            />
            <p className="text-xs text-slate-500 mt-3 mb-4">Sets assignment and ticket status to &quot;In Progress&quot;.</p>
            <div className="flex gap-2 justify-end">
              <button type="button" className="px-4 py-2 rounded-lg border border-slate-300 text-sm" onClick={() => setAssignOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                disabled={assignSaving}
                onClick={submitAssign}
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium disabled:opacity-50"
              >
                {assignSaving ? 'Saving…' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
